import { Flex, Statistic } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  LineOutlined,
} from "@ant-design/icons";
import { SegmentedOrDropdown, ShrinkingButton, StatCard } from "~/components";
import { COLORS, formatMoney } from "~/utils";
import type { HomePageAnalyticsType } from "../../loaders";

export type HomeAnalyticsCardsProps = {
  analytics: HomePageAnalyticsType;
  verySmallScreen: boolean;
};

export const HomeAnalyticsCards = ({
  analytics,
  verySmallScreen,
}: HomeAnalyticsCardsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const monthlyOrAnnually = [t("Monthly"), t("Yearly")];
  const [incomeTab, setIncomeTab] = useState(0);
  const [debtTab, setDebtTab] = useState(0);
  const [cashFlowTab, setCashFlowTab] = useState(0);

  const income =
    incomeTab === 0 ? analytics.monthlyIncome : analytics.yearlyIncome;
  const debt = debtTab === 0 ? analytics.monthlyExpenses : analytics.yearlyExpenses;
  const net = cashFlowTab === 0 ? analytics.monthlyNet : analytics.yearlyNet;

  const getColorFor = (num: number) => {
    if (num < 0) return COLORS.crimson_red;
    if (num > 0) return COLORS.blue;
    return COLORS.bright_lemon;
  };

  const getIconFor = (num: number) => {
    if (num < 0) return <ArrowDownOutlined />;
    if (num > 0) return <ArrowUpOutlined />;
    return <LineOutlined />;
  };

  return (
    <>
      <StatCard
        title={t("Rental_Income")}
        description={
          t("Rental_income_meaning") +
          (incomeTab === 0 ? t("Monthly") : t("Yearly"))
        }
        action={
          <SegmentedOrDropdown
            items={monthlyOrAnnually}
            selected={incomeTab}
            setSelected={setIncomeTab}
            useDropdown={verySmallScreen}
          />
        }
      >
        <Flex style={{ flexWrap: "wrap" }}>
          <Statistic
            value={income}
            valueStyle={{ color: getColorFor(income) }}
            formatter={(val) => formatMoney(Number(val))}
          />
          <ShrinkingButton
            small={verySmallScreen}
            onClick={() => navigate("/finance?tab=analytics")}
            type="primary"
            style={{ marginLeft: "auto" }}
          >
            {t("Details")}
          </ShrinkingButton>
        </Flex>
      </StatCard>
      <StatCard
        title={t("Debt")}
        description={
          t("Debt_meaning") + (debtTab === 0 ? t("Monthly") : t("Yearly"))
        }
        action={
          <SegmentedOrDropdown
            items={monthlyOrAnnually}
            selected={debtTab}
            setSelected={setDebtTab}
            useDropdown={verySmallScreen}
          />
        }
      >
        <Statistic
          value={debt}
          valueStyle={{ color: getColorFor(-debt) }}
          formatter={(val) => formatMoney(Number(val))}
        />
      </StatCard>
      <StatCard
        title={t("Cash_Flow")}
        description={
          t("Cash_flow_meaning") +
          (cashFlowTab === 0 ? t("Monthly") : t("Yearly"))
        }
        action={
          <SegmentedOrDropdown
            items={monthlyOrAnnually}
            selected={cashFlowTab}
            setSelected={setCashFlowTab}
            useDropdown={verySmallScreen}
          />
        }
      >
        <Statistic
          value={Math.abs(net)}
          valueStyle={{ color: getColorFor(net) }}
          prefix={getIconFor(net)}
          formatter={(val) => formatMoney(Number(val))}
        />
      </StatCard>
    </>
  );
};
